import { compactAgeLabel } from "@healthos/shared";
import { ArrowLeft, ShieldCheck } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { PageSectionHeader } from "../../components/page-header";
import { useMorphNavigate } from "../../hooks/use-morph-navigate";

/**
 * Shared chrome for the ops sub-pages: the shared PageSectionHeader recipe
 * inside the ops band (full-width hairline + paper background, content
 * aligned to the same 1480px container as OpsPageBody). The band wrapper is
 * a div because PageSectionHeader supplies the header landmark itself.
 */
export function OpsPageHeader({
  eyebrow,
  title,
  description,
  actions,
  tabs,
  back,
}: {
  /** Where this page came from, shown above the title as a way back. */
  back?: { to: string; label: string };
  eyebrow: string;
  title: ReactNode;
  description: string;
  actions?: ReactNode;
  /**
   * Optional tab row pinned to the bottom of the band, so the band's
   * hairline doubles as the tab baseline. Callers own the row container
   * (a nav for routed tabs, a role="tablist" for stateful ones) and style
   * each tab with opsTab() + OpsTabUnderline.
   */
  tabs?: ReactNode;
}) {
  return (
    <div className="border-border-default border-b bg-surface-elevated">
      <div className="mx-auto max-w-[1480px] px-6 pt-5">
        {back ? <BackLink to={back.to} label={back.label} /> : null}
        <PageSectionHeader
          as="h1"
          eyebrow={[eyebrow]}
          title={title}
          subtitle={description}
          skipEnter
          trailing={
            actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : undefined
          }
        />
        {tabs ? <div className="-mt-2">{tabs}</div> : null}
      </div>
    </div>
  );
}

function BackLink({ to, label }: { to: string; label: string }) {
  const { onLinkClick } = useMorphNavigate();
  return (
    <Link
      to={to}
      onClick={onLinkClick(to)}
      className="group -ml-2 mb-2 inline-flex items-center gap-2 rounded-full px-2 py-1 font-medium text-ink-secondary text-sm transition-colors hover:bg-surface-track/50 hover:text-ink-primary focus-visible:outline-2 focus-visible:outline-teal-600"
    >
      <ArrowLeft
        size={16}
        aria-hidden
        className="transition-transform duration-150 ease-out group-hover:-translate-x-0.5"
      />
      {label}
    </Link>
  );
}

/** Tab recipe for the OpsPageHeader tabs slot. Pair with OpsTabUnderline. */
export function opsTab(isActive: boolean): string {
  return `relative flex shrink-0 cursor-pointer items-center whitespace-nowrap px-4 py-3 font-medium text-sm transition-colors duration-150 ${
    isActive ? "text-ink-primary" : "text-ink-tertiary hover:text-ink-primary"
  }`;
}

/** Active-tab indicator, flush against the band hairline. */
export function OpsTabUnderline() {
  return <span className="absolute right-3 bottom-0 left-3 h-0.5 rounded-full bg-teal-600" />;
}

/** Header pill for admin surfaces whose access is role-gated. */
export function OpsRbacBadge() {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-surface-secondary px-4 py-2 font-mono font-semibold text-ink-eyebrow text-xs uppercase tracking-[0.14em]">
      <ShieldCheck size={15} weight="regular" />
      RBAC controlled
    </span>
  );
}

export function OpsPageBody({ children }: { children: ReactNode }) {
  return <div className="mx-auto max-w-[1480px] space-y-5 px-6 py-6">{children}</div>;
}

/**
 * The card surface every operations panel sits on. `OpsCard` is the version
 * with a header; surfaces that own their own layout (grids, flex columns,
 * charts) apply the constant directly, composing with `cn` so a caller's own
 * utilities win over the recipe rather than resolving by stylesheet order.
 */
export const OPS_PANEL =
  "overflow-hidden rounded-2xl border border-border-default bg-surface-elevated shadow-card";

export function OpsCard({
  title,
  subtitle,
  actions,
  children,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={OPS_PANEL}>
      <div className="flex items-start justify-between gap-3 border-border-default border-b bg-cream/40 px-4 py-2.5">
        <div className="min-w-0">
          <h2 className="font-semibold text-ink-primary text-sm">{title}</h2>
          {subtitle ? (
            <p className="mt-0.5 font-mono text-xs text-ink-tertiary uppercase tracking-wider">
              {subtitle}
            </p>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

export function OpsErrorState({ error }: { error: string }) {
  return (
    <div className="min-h-[40vh] p-8">
      <p className="text-sm text-attention-urgent-ink">Couldn't load data: {error}</p>
    </div>
  );
}

export function OpsLoading({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-3 p-4">
      {Array.from({ length: rows }, (_, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length skeleton placeholder; nothing to reorder.
          key={`skel-${i}-${rows}`}
          className="h-10 animate-pulse rounded-md bg-surface-track/40"
          aria-hidden
        />
      ))}
    </div>
  );
}

/** "12m ago" for the past, "in 7h" for the future, on the same compact steps. */
export function formatRelativeTime(iso: string | null, now: number = Date.now()): string {
  if (!iso) return "never";
  const at = new Date(iso).getTime();
  if (at <= now) return compactAgeLabel(at, now);
  const ahead = compactAgeLabel(now, at);
  return ahead === "just now" ? ahead : `in ${ahead.replace(/ ago$/, "")}`;
}

const USD_FORMAT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
const NUMBER_FORMAT = new Intl.NumberFormat("en-US");

export function formatUSD(n: number, opts: { compact?: boolean } = {}): string {
  if (opts.compact && Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (opts.compact && Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return USD_FORMAT.format(n);
}

export function formatNumber(n: number): string {
  return NUMBER_FORMAT.format(n);
}

export function formatPct(n: number, digits = 0): string {
  return `${(n * 100).toFixed(digits)}%`;
}

/** Triggers a browser download of an in-memory CSV string. Shared by every
 *  ops CSV export so there's one place that owns the blob-URL lifecycle
 *  (create, click, revoke). */
export function downloadCsv(filename: string, csv: string): void {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}
